import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { X, Download, Loader2, AlertCircle, ZoomIn, ZoomOut } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import axios from 'axios';
import { toast } from 'sonner';

// Match the pdf.js worker the rest of the app uses (e-signature editor /
// public signing). Setting it here too is idempotent and guarantees the
// worker is configured even if this modal mounts before those pages.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Universal in-page document preview modal.
 *
 * PDFs are rendered with pdf.js (react-pdf) into a <canvas> — NOT an <iframe>.
 * An <iframe>/<embed> relies on the browser's built-in PDF viewer, which
 * renders BLANK whenever the user/org has Chrome's "Download PDFs instead of
 * automatically opening them" policy enabled (the cause of the long-standing
 * blank-preview reports). Canvas rendering works regardless of that setting.
 *
 * Images render in an <img>. A tiny Range probe still detects files that are
 * gone from the server so we show a clear "re-upload" message instead of a
 * silent failure.
 */
export function DocumentPreviewModal({ open, onClose, previewUrl, downloadUrl, fileName, title }) {
  const inferredType = useMemo(() => {
    const ext = (fileName || previewUrl || '').toLowerCase().split('.').pop().split('?')[0];
    const map = {
      pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg',
      jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
      heic: 'image/heic', heif: 'image/heif', bmp: 'image/bmp',
      tif: 'image/tiff', tiff: 'image/tiff',
    };
    return map[ext] || 'application/pdf';
  }, [fileName, previewUrl]);

  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(800);
  const [tokenizedPreview, setTokenizedPreview] = useState(null);
  const [tokenizedDownload, setTokenizedDownload] = useState(null);
  const containerRef = useRef(null);

  const isPdf = inferredType.includes('pdf');
  const isImage = inferredType.startsWith('image/');

  // Protected file endpoints require auth. Browser embeds (react-pdf <Document>,
  // <img>) can't send an Authorization header, so we fetch a short-lived file
  // token and pass it as a ?token= query param.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const withToken = (url, t) => (url ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(t)}` : url);
    setTokenizedPreview(null);
    setTokenizedDownload(null);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API}/auth/file-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setTokenizedPreview(withToken(previewUrl, data.token));
        setTokenizedDownload(withToken(downloadUrl, data.token));
      } catch {
        if (cancelled) return;
        setTokenizedPreview(previewUrl);
        setTokenizedDownload(downloadUrl);
      }
    })();
    return () => { cancelled = true; };
  }, [open, previewUrl, downloadUrl]);

  // Reset state every time we open / change document
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNumPages(0);
    setScale(1);
  }, [open, previewUrl]);

  // Measure the available width so PDF pages fit the modal nicely.
  useEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const w = containerRef.current?.clientWidth;
      if (w) setPageWidth(Math.min(Math.max(w - 48, 320), 1000));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  // Fail-fast detection of files wiped from BOTH disk and GridFS, so the user
  // gets an actionable message instead of a stuck spinner.
  useEffect(() => {
    if (!open || !tokenizedPreview) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(tokenizedPreview, {
          headers: { Range: 'bytes=0-0' },
          responseType: 'arraybuffer',
        });
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404) {
          setError('Original file is no longer on the server — please re-upload it.');
        } else if (status >= 400) {
          setError(`Preview failed to load (error ${status}). Try Download, or re-upload the document.`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, tokenizedPreview]);

  const onDocLoad = useCallback(({ numPages: n }) => setNumPages(n), []);
  const onDocError = useCallback(() => {
    setError('This PDF could not be rendered. Try Download to open it, or re-upload the document.');
  }, []);

  const downloadFile = async () => {
    if (!tokenizedDownload) return;
    try {
      const res = await axios.get(tokenizedDownload, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      let chosenName = fileName || '';
      try {
        const cd = res.headers['content-disposition'] || '';
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        if (m && m[1]) {
          const headerName = decodeURIComponent(m[1].trim());
          if (!chosenName || !/\.[a-z0-9]{2,5}$/i.test(chosenName)) chosenName = headerName;
        }
      } catch { /* ignore */ }
      if (!chosenName || !/\.[a-z0-9]{2,5}$/i.test(chosenName)) {
        const mime = (res.headers['content-type'] || '').toLowerCase();
        const extMap = {
          'application/pdf': '.pdf', 'image/png': '.png', 'image/jpeg': '.jpg',
          'image/gif': '.gif', 'image/webp': '.webp', 'image/heic': '.heic',
          'image/tiff': '.tif', 'image/bmp': '.bmp',
        };
        const ext = extMap[mime.split(';')[0].trim()] || '';
        chosenName = (chosenName || title || 'document') + ext;
      }
      a.download = chosenName;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    }
  };

  if (!open) return null;

  const effectiveWidth = Math.round(pageWidth * scale);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="document-preview-modal"
    >
      <div
        className="bg-background rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium truncate" title={title || fileName}>{title || fileName || 'Document preview'}</h3>
            {fileName && title && fileName !== title && (
              <p className="text-xs text-muted-foreground truncate">{fileName}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {isPdf && !error && numPages > 0 && (
              <div className="hidden sm:flex items-center gap-1 mr-1">
                <button
                  type="button"
                  onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))}
                  className="p-1.5 rounded-md border hover:bg-muted"
                  data-testid="preview-zoom-out"
                  aria-label="Zoom out"
                >
                  <ZoomOut size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setScale((s) => Math.min(2.5, +(s + 0.25).toFixed(2)))}
                  className="p-1.5 rounded-md border hover:bg-muted"
                  data-testid="preview-zoom-in"
                  aria-label="Zoom in"
                >
                  <ZoomIn size={14} />
                </button>
              </div>
            )}
            {downloadUrl && !error && (
              <button
                type="button"
                onClick={downloadFile}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border hover:bg-muted"
                data-testid="preview-download-btn"
              >
                <Download size={14} />
                Download
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-muted"
              data-testid="preview-close-btn"
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-muted/30 flex flex-col items-center relative py-4"
        >
          {error && (
            <div className="flex flex-col items-center text-center px-6 max-w-md gap-3 m-auto">
              <AlertCircle size={40} className="text-red-500" />
              <p className="text-sm font-medium text-foreground">{error}</p>
              <p className="text-xs text-muted-foreground">
                You can still delete this record from the document list to clean it up.
              </p>
            </div>
          )}

          {!error && tokenizedPreview && isPdf && (
            <Document
              key={tokenizedPreview}
              file={tokenizedPreview}
              onLoadSuccess={onDocLoad}
              onLoadError={onDocError}
              loading={
                <div className="flex flex-col items-center justify-center text-muted-foreground gap-2 m-auto py-16">
                  <Loader2 size={28} className="animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              }
              error={
                <div className="flex flex-col items-center text-center px-6 max-w-md gap-3 m-auto py-16">
                  <AlertCircle size={40} className="text-red-500" />
                  <p className="text-sm font-medium">This PDF could not be rendered. Try Download instead.</p>
                </div>
              }
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="mb-4 shadow-lg bg-white" data-testid={`preview-pdf-page-${i + 1}`}>
                  <Page
                    pageNumber={i + 1}
                    width={effectiveWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          )}

          {!error && tokenizedPreview && isImage && (
            <img
              key={tokenizedPreview}
              src={tokenizedPreview}
              alt={title || 'Preview'}
              className="max-w-full max-h-full object-contain bg-white m-auto"
              onError={() => setError('Failed to load preview')}
              data-testid="preview-image"
            />
          )}

          {!error && previewUrl && !isPdf && !isImage && (
            <div className="flex flex-col items-center text-center px-6 max-w-md gap-3 m-auto">
              <AlertCircle size={40} className="text-muted-foreground" />
              <p className="text-sm">
                This file type can't be previewed in-browser. Click Download to view it.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentPreviewModal;
